/**
 * Release Notes Load More Web Component
 * 
 * Follows SOLID principles:
 * - Single Responsibility: Only handles section visibility
 * - Open/Closed: Extensible via data attributes
 * - Dependency Inversion: Depends on abstract DOM structure
 * 
 * Performance optimizations:
 * - Caches DOM queries
 * - Uses requestAnimationFrame for DOM updates
 * - Properly cleans up event listeners
 */
class SaReleaseNotesLoadMore extends HTMLElement {
  constructor() {
    super();
    
    // Private properties
    this._hiddenSections = null;
    this._visibleCount = 0;
    this._itemsPerLoad = parseInt(this.dataset.itemsPerLoad) || 2;
    this._sectionSelector = this.dataset.sectionSelector || '.release-notes-section';
    
    // Bind methods to preserve context
    this._handleClick = this._handleClick.bind(this);
  }

  connectedCallback() {
    this.classList.add('js-loaded');
    // Add a live region for screen reader announcements
    if (!this.querySelector('.loadmore-announcer')) {
      const announcer = document.createElement('div');
      announcer.className = 'loadmore-announcer visually-hidden';
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      this.appendChild(announcer);
    }
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._initialize());
    } else {
      this._initialize();
    }

    this._attachEventListeners();
  }

  disconnectedCallback() {
    this._removeEventListeners();
    this._cleanup();
  }

  /**
   * Initialize component
   * Reads existing section states (configured in Liquid)
   * Does NOT modify section states - only reads them
   */
  _initialize() {
    // Query all release-notes sections
    const allSections = Array.from(document.querySelectorAll(this._sectionSelector));
    
    // Debug: Log found sections
    if (allSections.length === 0) {
      console.warn('No release-notes sections found with selector:', this._sectionSelector);
      return;
    }
    
    // Validate that all sections have data-release-notes-index attribute
    const sectionsWithoutIndex = allSections.filter(section => 
      !section.hasAttribute('data-release-notes-index')
    );
    
    if (sectionsWithoutIndex.length > 0) {
      console.warn('Some release-notes sections are missing data-release-notes-index attribute:', sectionsWithoutIndex);
    }
    
    // Sort sections by their index attribute (already set by Liquid)
    allSections.sort((a, b) => {
      const indexA = parseInt(a.getAttribute('data-release-notes-index')) || 0;
      const indexB = parseInt(b.getAttribute('data-release-notes-index')) || 0;
      return indexA - indexB;
    });
    
    // Cache only hidden sections (already set by Liquid configuration)
    this._hiddenSections = allSections.filter(section => 
      section.classList.contains('release-notes-hidden')
    );
    
    // Count visible sections
    this._visibleCount = allSections.length - this._hiddenSections.length;
    
    this._updateButtonState();
  }

  /**
   * Attach event listeners
   * Uses event delegation for better performance
   */
  _attachEventListeners() {
    const button = this.querySelector('.release-notes-loadmore-button');
    if (button) {
      button.addEventListener('click', this._handleClick);
    }
  }

  /**
   * Remove event listeners
   * Prevents memory leaks
   */
  _removeEventListeners() {
    const button = this.querySelector('.release-notes-loadmore-button');
    if (button) {
      button.removeEventListener('click', this._handleClick);
    }
  }

  /**
   * Handle click event
   * Uses requestAnimationFrame to optimize DOM updates
   */
  _handleClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    if (button.classList.contains('loading')) {
      return;
    }
    
    // Show loading state
    button.classList.add('loading');
    const spinner = button.querySelector('.spinner');
    if (spinner) {
      spinner.classList.remove('hidden');
    }
    
    // Use requestAnimationFrame for smooth DOM updates
    requestAnimationFrame(() => {
      this._loadMore();
      
      // Remove loading state after animation
      requestAnimationFrame(() => {
        button.classList.remove('loading');
        if (spinner) {
          spinner.classList.add('hidden');
        }
      });
    });
  }

  /**
   * Load more sections
   * Removes sections from cache array and displays them
   */
  _loadMore() {
    if (!this._hiddenSections || this._hiddenSections.length === 0) {
      console.warn('No hidden sections to show');
      return;
    }
    
    // Get sections to show (splice removes them from array)
    const sectionsToShow = this._hiddenSections.splice(0, this._itemsPerLoad);
    
    // Show each section
    sectionsToShow.forEach((section, index) => {
      // Use requestAnimationFrame with delay for staggered animation (optional)
      requestAnimationFrame(() => {
        section.classList.remove('release-notes-hidden');
        this._visibleCount++;
      });
    });

    // Announce to screen readers
    const announcer = this.querySelector('.loadmore-announcer');
    if (announcer) {
      const remaining = this._hiddenSections ? this._hiddenSections.length - sectionsToShow.length : 0;
      announcer.textContent = `${sectionsToShow.length} more items loaded.${remaining > 0 ? ` ${remaining} items remaining.` : ' All items loaded.'}`;
    }

    this._updateButtonState();
  }

  /**
   * Update button state
   * Hides button when all sections are visible
   */
  _updateButtonState() {
    const button = this.querySelector('.release-notes-loadmore-button');
    if (!button) {
      return;
    }
    
    if (!this._hiddenSections || this._hiddenSections.length === 0) {
      button.classList.add('hidden');
    } else {
      button.classList.remove('hidden');
    }
  }

  /**
   * Cleanup method
   * Prevents memory leaks
   */
  _cleanup() {
    this._hiddenSections = null;
    this._handleClick = null;
  }
}

// Register custom element
if (!customElements.get('sa-release-notes-loadmore')) {
  customElements.define('sa-release-notes-loadmore', SaReleaseNotesLoadMore);
}

